import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('gymMembership');
export interface GymMembershipInfoRec {
  id: string;
  userId: string;
  gymName?: string;
  membershipType?: string;
  monthlyCost?: number;
  contractLength?: string;
  membershipStartDate?: string;
  contractEndDate?: string;
  autopayEnabled?: boolean;
  paymentMethod?: string;
  membershipId?: string;
  accessCode?: string;
  branchLocation?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  website?: string;
  notes?: string;
  username?: string;
  password?: string;
  accountNumber?: string;
  isLocked?: boolean;
}

class GymMembershipInfoService {
  async getAllGymMembershipInfo(): Promise<GymMembershipInfoRec[]> {
    const response = await http.get('/api/gym-membership-info');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch gym membership information');
    }

    return response.data.data || [];
  }

  async getGymMembershipInfoById(id: string): Promise<GymMembershipInfoRec | null> {
    try {
      const response = await http.get(`/api/gym-membership-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch gym membership information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching gym membership information by ID:', error);
      return null;
    }
  }

  async createGymMembershipInfo(gymMembershipData: Partial<GymMembershipInfoRec>): Promise<GymMembershipInfoRec> {
    const response = await http.post('/api/gym-membership-info', gymMembershipData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save gym membership information');
    }

    return response.data.data;
  }

  async updateGymMembershipInfo(id: string, gymMembershipData: Partial<GymMembershipInfoRec>): Promise<GymMembershipInfoRec> {
    const response = await http.patch(`/api/gym-membership-info/${id}`, gymMembershipData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update gym membership information');
    }

    return response.data.data;
  }

  async deleteGymMembershipInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/gym-membership-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete gym membership information');
    }
  }

  async lockGymMembershipInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for gym membership ID:', id);
    const response = await http.post(`/api/gym-membership-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock gym membership information');
    }
  }

  async unlockGymMembershipInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for gym membership ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/gym-membership-info/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock gym membership information');
    }
  }

  async getGymMembershipAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/gym-membership-info/${id}/access-grants`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch gym membership access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching gym membership access grants:', error);
      return [];
    }
  }
}

export const gymMembershipInfoService = new GymMembershipInfoService();
export default gymMembershipInfoService;